import type { Theme } from '@withjoy/joykit/theme';
import { useTheme } from '@withjoy/joykit/theme';
import type { StyleSystemProps } from '@withjoy/joykit/styled-system';
import { animationTransition } from '@shared/utils/animationTransition';

type UseFocusBoxStylesArgs = {
  $isDisabled: boolean;
  $isInvalid: boolean;
  $isFocused: boolean;
  /**
   * A radius the caller pinned on the field itself -- the pill searches, the toolbar steppers. The
   * caller's override reaches the Root on its own, but not this hook's `::after`, so the ring has
   * to be told the same number or it hugs an 8px corner around a 24px pill.
   */
  $pinnedRadius?: StyleSystemProps['borderRadius'];
  /**
   * Draw no ring at all -- for a field whose wrapper supplies the focus indicator and which would
   * otherwise show two.
   *
   * This is the *declared* form of something five call sites already achieved by collision:
   * `Input.tsx` spreads `styles.Root` and then the caller's props, so a caller-supplied `_after`
   * replaces the contract's rather than merging into it. That is the same mechanism that silently
   * erased the ring at twelve fields whose authors only meant to set a radius. Suppression should
   * say so.
   */
  $suppressFocusRing?: boolean;
};

/**
 * The properties a field animates as it moves between rest, hover, focus and invalid. Exported so
 * that a component owning the field's own `transition` extends this list rather than replacing it.
 *
 * `border-color` is inert for `InputV2` -- its hairline stays `inputBorder` in every state, and the
 * ring carries the colour -- but the InputV1/TextArea fields on the same contract do swap it, so it
 * is carried here for them rather than used here.
 */
export const FIELD_STATE_TRANSITION_PROPERTIES = ['border-color', 'box-shadow'] as const;

/**
 * Hover and focus co-occur -- a mouse user's pointer stays over the field they just clicked into --
 * so there is one ring whose colour is a function of state rather than a hover shadow and a focus
 * shadow competing for the same edge. Rest is that same ring at `transparent`, which is what makes
 * rest -> hover -> focus read as one continuous colour animation.
 *
 * Precedence: disabled > invalid > focus > hover > rest.
 *
 * See doc/field-focus-consistency/PLAN.md.
 */
const ringColorFromState = ({ colors }: Theme, { $isDisabled, $isFocused, $isInvalid }: UseFocusBoxStylesArgs, $isHovered: boolean) => {
  if ($isDisabled) {
    return 'transparent';
  } else if ($isInvalid) {
    return colors.inputBorderError;
  } else if ($isFocused) {
    return colors.inputBorderFocused;
  } else if ($isHovered) {
    return colors.mono6;
  }
  return 'transparent';
};

const baseStyles: StyleSystemProps = {
  position: 'relative',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'inputBorder',
};

export const useFocusBoxStyles = (args: UseFocusBoxStylesArgs): StyleSystemProps => {
  const theme = useTheme();
  // One radius, every state. The field's geometry is fixed and only the ring's colour moves.
  const fieldRadius = args.$pinnedRadius ?? 'field';

  if (args.$suppressFocusRing) {
    // Geometry and transition still apply -- only the ring goes.
    return {
      ...baseStyles,
      borderRadius: fieldRadius,
      transition: animationTransition(...FIELD_STATE_TRANSITION_PROPERTIES),
    };
  }

  return {
    ...baseStyles,
    borderRadius: fieldRadius,
    transition: animationTransition(...FIELD_STATE_TRANSITION_PROPERTIES),

    // Hover state
    _hover: args.$isDisabled
      ? undefined
      : {
          _after: { boxShadow: `0px 0 0px 2px ${ringColorFromState(theme, args, true)}` },
        },

    // Box shadow styles
    _after: {
      background: 'none',
      display: args.$isDisabled ? 'none' : 'block',
      content: '" "',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none',
      transition: animationTransition(...FIELD_STATE_TRANSITION_PROPERTIES),
      borderRadius: fieldRadius,
      boxShadow: `0px 0 0px 2px ${ringColorFromState(theme, args, false)}`,
    },
  };
};
